<script>
import { Modal } from 'bootstrap';
import { useLeadMetaStore } from '@/stores/useLeadMetaStore';
import {
  getCardSources,
  createBusinessCard,
  updateBusinessCard,
  CARD_THEMES,
  SOCIAL_TYPES,
} from '@/utils/business-card';

const defaultVisibility = () => ({
  designation: true,
  department: true,
  company: true,
  phone: true,
  whatsapp: true,
  email: true,
  website: true,
  bio: true,
  social_links: true,
});

const defaultForm = () => ({
  user_id: '',
  designation: '',
  company: '',
  phone: '',
  whatsapp: '',
  email: '',
  website: '',
  bio: '',
  theme: 'modern',
  is_active: true,
  social_links: [],
  visibility: defaultVisibility(),
});

export default {
  inject: ['setLoading'],
  emits: ['updated'],
  data() {
    return {
      is_edit: false,
      card_id: null,
      form: defaultForm(),
      errors: {},
      existing_card_warning: false,
      themes: CARD_THEMES,
      social_types: SOCIAL_TYPES,
      visibility_fields: [
        { key: 'designation', label: 'Designation' },
        { key: 'department', label: 'Department' },
        { key: 'company', label: 'Company' },
        { key: 'phone', label: 'Phone' },
        { key: 'whatsapp', label: 'WhatsApp' },
        { key: 'email', label: 'Email' },
        { key: 'website', label: 'Website' },
        { key: 'bio', label: 'Bio' },
        { key: 'social_links', label: 'Social Links' },
      ],
      auto_filled: {},
    };
  },

  computed: {
    users() {
      return useLeadMetaStore().users || [];
    },
  },

  methods: {
    reset() {
      this.form = defaultForm();
      this.errors = {};
      this.existing_card_warning = false;
      this.card_id = null;
      this.auto_filled = {};
    },

    async add() {
      this.reset();
      this.is_edit = false;
      await useLeadMetaStore().loadAll();
    },

    async edit(row) {
      this.reset();
      this.is_edit = true;
      this.card_id = row.id;
      await useLeadMetaStore().loadAll();

      this.form = {
        user_id: row.user_id,
        designation: row.designation || '',
        company: row.company || '',
        phone: row.phone || '',
        whatsapp: row.whatsapp || '',
        email: row.email || '',
        website: row.website || '',
        bio: row.bio || '',
        theme: row.theme || 'modern',
        is_active: !!row.is_active,
        social_links: JSON.parse(JSON.stringify(row.social_links || [])),
        visibility: { ...defaultVisibility(), ...(row.visibility || {}) },
      };
    },

    // Prefill fields when a user is selected (create mode only)
    async onUserChange(user_id) {
    this.existing_card_warning = false;
    this.errors = {};
    if (this.is_edit || !user_id) return;

    this.setLoading(true);
    try {
      const response = await getCardSources(user_id);

      // Ignore stale response if the user changed selection meanwhile
      if (this.form.user_id !== user_id) return;

      const data = response.data.data;
      if (data.card) {
        this.existing_card_warning = true;
        return;
      }

      const s = data.sources || {};
      const incoming = {
        designation: s.designation || '',
        company: s.company || '',
        phone: s.phone || '',
        whatsapp: s.phone || '',
        email: s.email || '',
      };

      Object.entries(incoming).forEach(([key, value]) => {
        const current = this.form[key];
        const untouched = !current || current === this.auto_filled[key];
        if (untouched) {
          this.form[key] = value;
          this.auto_filled[key] = value;
        }
      });
    } catch (error) {
      if (error.type === 'validation') {
        this.errors = error.errors || {};
        const first = Object.values(this.errors)[0];
        this.$swalToast(first ? first[0] : 'Validation error', 'error');
      } else {
        this.$swalToast(error.message, 'error');
      }
    } finally {
      this.setLoading(false);
    }
  },

    addSocial() {
      this.form.social_links.push({ type: 'linkedin', url: '' });
    },

    removeSocial(index) {
      this.form.social_links.splice(index, 1);
    },

    err(key) {
      const e = this.errors[key];
      return e ? e[0] : '';
    },

    async save() {
      this.errors = {};

      // Drop social rows with an empty URL
      const payload = {
        ...this.form,
        social_links: this.form.social_links.filter(s => s.url && s.url.trim()),
      };

      this.setLoading(true);
      try {
        const response = this.is_edit
          ? await updateBusinessCard(this.card_id, payload)
          : await createBusinessCard(payload);

        this.$swalToast(response.data.message || 'Saved');
        this.$emit('updated');

        const modal = Modal.getInstance(this.$refs.modalEl);
        if (modal) modal.hide();
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors || {};
          const first = Object.values(this.errors)[0];
          this.$swalToast(first ? first[0] : 'Validation error', 'error');
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<style scoped>
.f-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}
.f-error {
  font-size: 11px;
  color: #dc2626;
  margin-top: 2px;
}
.f-section {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  text-transform: uppercase;
  letter-spacing: .3px;
  border-top: 1px solid #f1f5f9;
  padding-top: 14px;
  margin: 16px 0 10px;
}
</style>

<template>
  <div class="modal fade" id="business_card_modal" tabindex="-1" data-bs-backdrop="static" ref="modalEl">
    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
      <div class="modal-content">

        <div class="modal-header">
          <h5 class="modal-title">{{ is_edit ? 'Edit' : 'Add' }} Business Card</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">

          <!-- Existing card warning -->
          <div v-if="existing_card_warning" class="alert alert-warning py-2" style="font-size:12.5px;">
            This user already has a business card. Each user can have only one card,
            so edit the existing card from the list.
          </div>

          <div class="row g-3">

            <!-- User -->
            <div class="col-md-6">
              <div class="f-label">User *</div>
              <vue-select
                :options="users"
                label="name"
                v-model="form.user_id"
                :reduce="u => u.id"
                placeholder="Select user"
                :clearable="false"
                :disabled="is_edit"
                @update:modelValue="onUserChange"
              />
              <div class="f-error" v-if="err('user_id')">{{ err('user_id') }}</div>
            </div>

            <!-- Theme -->
            <div class="col-md-4">
              <div class="f-label">Theme</div>
              <vue-select
                :options="themes"
                label="label"
                v-model="form.theme"
                :reduce="t => t.value"
                :clearable="false"
              />
              <div class="f-error" v-if="err('theme')">{{ err('theme') }}</div>
            </div>

            <!-- Active -->
            <div class="col-md-2">
              <div class="f-label">Active</div>
              <div class="form-check form-switch mt-2">
                <input class="form-check-input" type="checkbox" role="switch" v-model="form.is_active">
              </div>
            </div>

            <!-- Designation -->
            <div class="col-md-6">
              <div class="f-label">Designation</div>
              <input type="text" class="form-control" v-model="form.designation" placeholder="Sales Manager">
              <div class="f-error" v-if="err('designation')">{{ err('designation') }}</div>
            </div>

            <!-- Company -->
            <div class="col-md-6">
              <div class="f-label">Company</div>
              <input type="text" class="form-control" v-model="form.company" placeholder="ABC Enterprises">
              <div class="f-error" v-if="err('company')">{{ err('company') }}</div>
            </div>

            <!-- Phone -->
            <div class="col-md-6">
              <div class="f-label">Phone</div>
              <input type="text" class="form-control" v-model="form.phone">
              <div class="f-error" v-if="err('phone')">{{ err('phone') }}</div>
            </div>

            <!-- WhatsApp -->
            <div class="col-md-6">
              <div class="f-label">WhatsApp</div>
              <input type="text" class="form-control" v-model="form.whatsapp">
              <div class="f-error" v-if="err('whatsapp')">{{ err('whatsapp') }}</div>
            </div>

            <!-- Email -->
            <div class="col-md-6">
              <div class="f-label">Email</div>
              <input type="email" class="form-control" v-model="form.email">
              <div class="f-error" v-if="err('email')">{{ err('email') }}</div>
            </div>

            <!-- Website -->
            <div class="col-md-6">
              <div class="f-label">Website</div>
              <input type="text" class="form-control" v-model="form.website" placeholder="https://example.com">
              <div class="f-error" v-if="err('website')">{{ err('website') }}</div>
            </div>

            <!-- Bio -->
            <div class="col-12">
              <div class="f-label">Bio</div>
              <textarea class="form-control" rows="3" v-model="form.bio"></textarea>
              <div class="f-error" v-if="err('bio')">{{ err('bio') }}</div>
            </div>
          </div>

          <!-- Social links -->
          <div class="f-section d-flex align-items-center justify-content-between">
            <span>Social Links</span>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="addSocial">
              <i class="ti ti-plus me-1"></i>Add
            </button>
          </div>

          <div v-if="!form.social_links.length" style="font-size:12px; color:#94a3b8;">
            No social links added.
          </div>

          <div class="row g-2 mb-2 align-items-start" v-for="(s, i) in form.social_links" :key="i">
            <div class="col-md-4">
              <vue-select
                :options="social_types"
                label="label"
                v-model="s.type"
                :reduce="t => t.value"
                :clearable="false"
              />
            </div>
            <div class="col">
              <input type="text" class="form-control" v-model="s.url" placeholder="https://...">
              <div class="f-error" v-if="err(`social_links.${i}.url`)">{{ err(`social_links.${i}.url`) }}</div>
            </div>
            <div class="col-auto">
              <button type="button" class="btn btn-icon btn-outline-danger" @click="removeSocial(i)">
                <i class="ti ti-trash"></i>
              </button>
            </div>
          </div>

          <!-- Visibility -->
          <div class="f-section">Visible on public card</div>
          <div class="row g-2">
            <div class="col-6 col-md-4" v-for="f in visibility_fields" :key="f.key">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch"
                  :id="`vis_${f.key}`" v-model="form.visibility[f.key]">
                <label class="form-check-label" :for="`vis_${f.key}`" style="font-size:12.5px;">{{ f.label }}</label>
              </div>
            </div>
          </div>

        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-primary" @click="save" :disabled="existing_card_warning || !!err('user_id')">
            {{ is_edit ? 'Update' : 'Create' }}
          </button>
        </div>

      </div>
    </div>
  </div>
</template>